import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "../../components/AppShell";
import { api } from "../../lib/api";
import { logError } from "../../lib/log";
import { AlertTriangle, Loader2, TrendingDown, ArrowRight } from "lucide-react";

const fmtL = (v) => v == null || isNaN(v) ? "—" : "₹" + Number(v).toFixed(1) + "L";

export default function MisOutliers() {
  const [outliers, setOutliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [opexThreshold, setOpexThreshold] = useState(80);
  const [jumpPct, setJumpPct] = useState(25);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/mis-analysis/outliers?opex_threshold=${opexThreshold}&line_jump_pct=${jumpPct}`);
        if (!cancelled) setOutliers(data.outliers || []);
      } catch (e) { logError("outliers fetch failed", e); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [opexThreshold, jumpPct]);

  return (
    <AppShell title="Outlier Watch" subtitle="Auto-flagged expense anomalies" persona="mis">
      <section className="bg-white border border-zinc-200 rounded-xl p-5 mb-5" data-testid="mis-outliers-controls">
        <div className="flex items-center gap-5 flex-wrap">
          <div className="flex items-center gap-2 flex-shrink-0">
            <AlertTriangle className="w-4 h-4 text-rose-600" />
            <span className="font-display font-bold text-[14px]">Thresholds</span>
          </div>
          <label className="flex items-center gap-2 text-[12px]">
            <span className="text-zinc-600">Opex ratio above</span>
            <input
              type="number"
              value={opexThreshold}
              onChange={(e) => setOpexThreshold(Math.max(0, Math.min(200, Number(e.target.value) || 0)))}
              data-testid="opex-threshold-input"
              className="w-16 border border-zinc-200 rounded px-2 py-1 tabular-nums text-right text-[12px]"
            />
            <span className="text-zinc-500">%</span>
          </label>
          <label className="flex items-center gap-2 text-[12px]">
            <span className="text-zinc-600">Single line item jump MoM</span>
            <input
              type="number"
              value={jumpPct}
              onChange={(e) => setJumpPct(Math.max(0, Math.min(500, Number(e.target.value) || 0)))}
              data-testid="jump-threshold-input"
              className="w-16 border border-zinc-200 rounded px-2 py-1 tabular-nums text-right text-[12px]"
            />
            <span className="text-zinc-500">%</span>
          </label>
          <div className="ml-auto text-[11.5px] text-zinc-500">
            <span className="font-bold text-rose-700 tabular-nums" data-testid="outliers-count">{outliers.length}</span> properties flagged
          </div>
        </div>
      </section>

      {loading ? (
        <div className="flex items-center justify-center h-64 text-zinc-500" data-testid="mis-outliers-loading">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Scanning Company Level…
        </div>
      ) : outliers.length === 0 ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-12 text-center" data-testid="mis-outliers-empty">
          <div className="font-display font-black text-xl text-emerald-800">All clear ✓</div>
          <p className="text-[12.5px] text-emerald-700 mt-1">No properties match your thresholds. Lower the bar to surface more.</p>
        </div>
      ) : (
        <ul className="space-y-2.5" data-testid="mis-outliers-list">
          {outliers.map((o) => (
            <li
              key={o.location}
              onClick={() => navigate(`/mis/property-tracker?location=${encodeURIComponent(o.location)}`)}
              data-testid={`outlier-${o.location}`}
              className="bg-white border-2 border-rose-200 rounded-xl p-4 hover:border-rose-400 hover:shadow-md cursor-pointer transition-all flex items-center gap-4"
            >
              <span className="flex-shrink-0 w-10 h-10 rounded-lg bg-rose-50 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-rose-600" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-display font-black text-[16px] text-zinc-900">{o.location}</h3>
                  <span className="text-[10.5px] text-zinc-500">{o.state} · {o.month}</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {o.flags.map((f) => (
                    <span key={f} className="text-[10.5px] bg-rose-50 border border-rose-200 text-rose-800 rounded px-2 py-0.5">{f}</span>
                  ))}
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-[9.5px] uppercase tracking-wider font-bold text-zinc-500">EBITDA</div>
                <div className={`font-display font-black tabular-nums ${o.ebitda_lakhs < 0 ? "text-rose-800" : "text-emerald-800"}`} style={{ fontSize: 18, lineHeight: 1.4 }}>
                  {o.ebitda_lakhs < 0 && <TrendingDown className="inline w-3.5 h-3.5 mr-0.5" />}
                  {fmtL(o.ebitda_lakhs)}
                </div>
                <div className="text-[9.5px] text-rose-700 mt-0.5">Opex {o.opex_ratio_pct}%</div>
              </div>
              <ArrowRight className="w-4 h-4 text-zinc-300" />
            </li>
          ))}
        </ul>
      )}
    </AppShell>
  );
}
